/**
 * Utility functions for the Coffee King theme
 * All functions are exposed on window.utils for easy access
 *
 * URL structure:
 *   English (EU): coffeekingdirect.eu/
 *   Spanish (EU): coffeekingdirect.eu/es/
 */

(function () {
  if (typeof window === 'undefined') return;

  /**
   * Get product handle from cart item or product URL
   * @param {Object} item - Cart item or product object with url/product_url
   * @returns {string|null} Product handle or null
   */
  function getProductHandleFromItem(item) {
    if (!item) return null;
    const url = String(item.url || item.product_url || '');
    const m = url.match(/\/products\/([^/?]+)/);
    return m ? m[1] : null;
  }

  /**
   * Get selling plan ID from cart item
   * @param {Object} item - Cart item with selling_plan_allocation
   * @returns {string} Selling plan ID as string, or empty string if none
   */
  function getSellingPlanIdFromCartItem(item) {
    if (!item) return '';
    const alloc = item.selling_plan_allocation;
    const planId = alloc?.selling_plan_id || alloc?.selling_plan?.id || null;
    return planId ? String(planId) : '';
  }

  /**
   * Get locale from common sources (data attributes, Shopify, or default)
   * @returns {string|undefined} Locale string or undefined
   */
  function getLocale() {
    if (typeof document === 'undefined') return undefined;

    // Try cart drawer data attribute
    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer) {
      const locale = cartDrawer.getAttribute('data-locale');
      if (locale) return locale;
    }

    // Try product price element
    const priceEl = document.querySelector('[data-product-add-to-cart-price]');
    if (priceEl) {
      const locale = priceEl.getAttribute('data-locale');
      if (locale) return locale;
    }

    // Try Shopify's global locale
    if (window.Shopify && window.Shopify.locale) {
      return window.Shopify.locale;
    }

    return undefined;
  }

  /**
   * Normalize inventory quantity: clamp negative values to 0
   * @param {number|string|null|undefined} quantity - Inventory quantity
   * @returns {number} Normalized quantity (0 if negative or invalid)
   */
  function normalizeInventoryQuantity(quantity) {
    if (quantity === null || quantity === undefined) return 0;
    const qty = typeof quantity === 'string' ? parseInt(quantity, 10) : quantity;
    if (isNaN(qty)) return 0;
    return Math.max(0, qty);
  }

  /**
   * Check if a value is empty (null, undefined, empty string, or string 'null'/'undefined')
   * @param {*} value - Value to check
   * @returns {boolean} True if value is empty
   */
  function isEmptyValue(value) {
    return (
      value === null ||
      value === undefined ||
      value === '' ||
      value === 'null' ||
      value === 'undefined'
    );
  }

  /**
   * Normalize a value to a string or empty string
   * Handles null, undefined, and string representations of null/undefined
   * @param {*} value - Value to normalize
   * @returns {string} Normalized string value or empty string
   */
  function normalizeStringValue(value) {
    if (isEmptyValue(value)) {
      return '';
    }
    return String(value);
  }

  /**
   * Get currency from common sources (data attributes, Shopify, or default)
   * @returns {string} Currency code (defaults to 'USD')
   */
  function getCurrency() {
    if (typeof document === 'undefined') return 'USD';

    // Try cart drawer data attribute
    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer) {
      const currency = cartDrawer.getAttribute('data-currency');
      if (currency) return currency;
    }

    // Try product price element
    const priceEl = document.querySelector('[data-product-add-to-cart-price]');
    if (priceEl) {
      const currency = priceEl.getAttribute('data-currency');
      if (currency) return currency;
    }

    // Try Shopify's global currency
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }

    // Default fallback
    return 'USD';
  }

  /**
   * Format money from cents to currency string
   * Matches Shopify's money filter format (currency symbol before number)
   * @param {number} cents - Amount in cents
   * @param {Object} options - Formatting options
   * @param {string} options.locale - Locale string (auto-detected if not provided)
   * @param {string} options.currency - Currency code (auto-detected if not provided)
   * @returns {string} Formatted currency string
   */
  function formatMoney(cents, options = {}) {
    if (typeof cents !== 'number' || isNaN(cents)) {
      return '0.00';
    }

    const amount = cents / 100;
    const locale = options.locale || getLocale() || 'en';
    const currency = options.currency || getCurrency() || 'USD';

    try {
      const parts = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).formatToParts(amount);

      const currencySymbol = parts.find((part) => part.type === 'currency')?.value || currency;
      const numberParts = parts.filter((part) => part.type !== 'currency');
      const formattedNumber = numberParts.map((part) => part.value).join('');

      return currencySymbol + formattedNumber;
    } catch (e) {
      return amount.toFixed(2).replace('.', ',') + ' ' + currency;
    }
  }

  /**
   * Format money from cents (alias for formatMoney for backward compatibility)
   * @param {number} cents - Amount in cents
   * @param {Object} options - Formatting options
   * @returns {string} Formatted currency string
   */
  function formatMoneyFromCents(cents, options = {}) {
    return formatMoney(cents, options);
  }

  /**
   * Format date to readable format with ordinal suffix (e.g., "May 8th")
   * @param {Date} date - Date object to format
   * @param {Object} options - Formatting options
   * @param {string} options.locale - Locale string (auto-detected if not provided)
   * @returns {string} Formatted date string
   */
  function formatDeliveryDate(date, options = {}) {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }

    const day = date.getDate();
    const locale = options.locale || getLocale();
    const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);

    let suffix = 'th';
    if (day % 10 === 1 && day % 100 !== 11) {
      suffix = 'st';
    } else if (day % 10 === 2 && day % 100 !== 12) {
      suffix = 'nd';
    } else if (day % 10 === 3 && day % 100 !== 13) {
      suffix = 'rd';
    }

    return `${month} ${day}${suffix}`;
  }

  // Supported language prefixes (add more here if needed in future)
  const SUPPORTED_LANG_PREFIXES = ['es'];
  const DEFAULT_LANGUAGE = 'en';

  /**
   * Parse pathname into parts array
   * @param {string} pathname - Pathname to parse (defaults to current location)
   * @returns {string[]} Array of path segments (filtered empty strings)
   */
  function parsePathParts(pathname) {
    const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
    return String(path).split('/').filter(Boolean);
  }

  /**
   * Helper to normalize string (trim and lowercase)
   * @param {*} value - Value to normalize
   * @returns {string} Normalized lowercase trimmed string
   */
  function normalizeString(value) {
    return (normalizeStringValue(value) || '').trim().toLowerCase();
  }

  /**
   * Map Shopify market handle to URL market code
   * Kept for backward compatibility with other scripts that may reference this
   * @param {string} marketHandle - Market handle from Shopify
   * @returns {string} URL market code
   */
  function mapMarketHandleToUrlCode(marketHandle) {
    if (!marketHandle) return null;

    const handle = normalizeString(marketHandle);

    const marketMap = {
      'european-union': 'eu',
      eu: 'eu',
      'united-kingdom': 'uk',
      uk: 'uk',
      'united-states': 'us',
      us: 'us',
      canada: 'ca',
      ca: 'ca',
    };

    return marketMap[handle] || handle;
  }

  /**
   * Parse current URL to extract language and path remainder
   *
   * URL structure:
   *   /              → English homepage
   *   /es            → Spanish homepage
   *   /es/products/  → Spanish product page
   *   /products/     → English product page
   *
   * @param {string} pathname - Optional pathname (defaults to current location)
   * @returns {{lang: string, remainder: string[], market: string|null}}
   *   lang: 'en' or 'es'
   *   remainder: path segments after the language prefix
   *   market: always 'eu' (kept for backward compatibility)
   */
  function parseShopifyUrl(pathname) {
    const parts = parsePathParts(pathname);

    if (parts.length === 0) {
      return { lang: DEFAULT_LANGUAGE, remainder: [], market: 'eu' };
    }

    const firstSegment = normalizeString(parts[0]);

    if (SUPPORTED_LANG_PREFIXES.includes(firstSegment)) {
      return {
        lang: firstSegment,
        remainder: parts.slice(1),
        market: 'eu',
      };
    }

    return {
      lang: DEFAULT_LANGUAGE,
      remainder: parts,
      market: 'eu',
    };
  }

  /**
   * Build a URL for the given language
   *
   * URL structure:
   *   English: /products/my-product  →  /products/my-product
   *   Spanish: /products/my-product  →  /es/products/my-product
   *
   * @param {Object} options - URL building options
   * @param {string} options.lang - Target language ('en' or 'es')
   * @param {string[]} options.remainder - Path segments after language prefix
   * @param {string} options.pathname - Current pathname (used to detect remainder if not provided)
   * @returns {string} Built URL path
   */
  function buildShopifyUrl(options = {}) {
    const { lang = DEFAULT_LANGUAGE, remainder, pathname } = options;

    let currentRemainder;
    if (remainder !== undefined) {
      currentRemainder = Array.isArray(remainder) ? remainder : [];
    } else {
      const parsed = parseShopifyUrl(pathname);
      currentRemainder = parsed.remainder || [];
    }

    const normalizedLang = normalizeString(lang) || DEFAULT_LANGUAGE;
    const tail = currentRemainder.length ? `/${currentRemainder.join('/')}` : '';
    const search = typeof window !== 'undefined' ? window.location.search : '';

    if (normalizedLang === 'es') {
      return `/es${tail}`.replace(/\/{2,}/g, '/') + search;
    }

    // English: no prefix
    return (tail || '/').replace(/\/{2,}/g, '/') + search;
  }

  // Expose all utilities on window.utils
  window.utils = {
    getProductHandleFromItem,
    getSellingPlanIdFromCartItem,
    normalizeInventoryQuantity,
    isEmptyValue,
    normalizeStringValue,
    formatMoney,
    formatMoneyFromCents,
    formatDeliveryDate,
    getLocale,
    getCurrency,
    parsePathParts,
    parseLangMarketSegment: (seg) => {
      // Kept for backward compatibility - previously parsed 'en-eu' style segments
      // Now always returns the simple /es structure
      const s = normalizeString(seg);
      if (SUPPORTED_LANG_PREFIXES.includes(s)) return { lang: s, market: 'eu' };
      return { lang: DEFAULT_LANGUAGE, market: 'eu' };
    },
    parseShopifyUrl,
    buildShopifyUrl,
    mapMarketHandleToUrlCode,
  };

  /**
   * Get current language from URL
   * @returns {string} Current language code ('en' or 'es')
   */
  window.__ckGetCurrentLang = function () {
    const parsed = parseShopifyUrl(window.location.pathname);
    return parsed.lang || 'en';
  };

  // Mark utils as loaded
  window.utils._loaded = true;
})();

/**
 * Dependency check function - call this at the start of files that depend on utils
 * @param {string} fileName - Name of the file checking for utils (for error messages)
 * @throws {Error} If utils is not loaded
 */
window.requireUtils = function (fileName) {
  if (!window.utils || !window.utils._loaded) {
    const error = new Error(
      `[${fileName}] window.utils is not loaded. Ensure utils.js loads before this file.`
    );
    console.error(error.message);
    console.error('Loading order should be: utils.js -> other scripts');
    throw error;
  }
};